import { documentSchema } from "@prismicio/editor-fields"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { Document } from "@prismicio/types-internal/lib/content"
import { z } from "zod"

import { getDevToken } from "../auth/devToken"
import { env } from "../env"
import { getRepositoryName } from "../routing"
import { socketRequest } from "./ws"

interface TranslateDocumentArgs {
  uuid: string
  document: Document
  locale: string
  onMessage: (message: TranslateDocumentMessage) => void
  isObelixAiTranslateDocumentEnabled: boolean
}

export function translateDocument(args: TranslateDocumentArgs) {
  const {
    uuid,
    document,
    locale,
    onMessage,
    isObelixAiTranslateDocumentEnabled,
  } = args

  const repository = getRepositoryName()

  const wsUrl = isObelixAiTranslateDocumentEnabled
    ? new URL(env.PRISMIC_DOCUMENT_WEBSOCKET_BASE_URL)
    : new URL(env.PRISMIC_AI_SERVICE_WEBSOCKET_ENDPOINT)

  wsUrl.searchParams.set("repository", repository)
  wsUrl.searchParams.set("uuid", uuid)

  if (env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY === "token") {
    wsUrl.searchParams.set("authorization", getDevToken())
  }

  const translateDocumentUrl = isObelixAiTranslateDocumentEnabled
    ? new URL("translate-document", env.PRISMIC_DOCUMENT_BASE_URL)
    : new URL("translate-document", env.PRISMIC_AI_SERVICE_BASE_URL)

  translateDocumentUrl.searchParams.set("repository", repository)

  socketRequest({
    url: wsUrl,
    onOpen: () =>
      request(translateDocumentUrl, {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, document, locale },
        okType: z.unknown(),
      }),
    schema: translateDocumentMessageSchema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "translating":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

const translateDocumentMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("translating") }),
  z.object({
    status: z.literal("done"),
    document: documentSchema,
    model: z.string(),
    cost: z.number(),
    tokensIn: z.number(),
    tokensOut: z.number(),
  }),
  z.object({ status: z.literal("failed") }),
])

type TranslateDocumentMessage = z.infer<typeof translateDocumentMessageSchema>
